import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getApiBaseUrl } from "../../config";

const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const user = getState().user?.currentUser;
    if (user?.id) {
      headers.set("userid", user.id);
    }
    return headers;
  },
});

// Define our API service with endpoints
export const mvdApi = createApi({
  reducerPath: "mvdApi",
  baseQuery,
  tagTypes: ["MVD", "Companies"],
  endpoints: (builder) => ({
    // Get all MVDs
    getMVDs: builder.query({
      async queryFn(
        {
          pageSize = 10,
          nextToken = undefined,
          page = 1,
          companyProfileId = null,
          limit = 100,
        } = {},
        queryApi,
        extraOptions,
        baseQuery
      ) {
        const monthNames = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const norm = (m) => {
          const created = m.createdAt || m.CREATEDTIME;
          let createdIso;
          try {
            createdIso = created
              ? new Date(created).toISOString()
              : new Date().toISOString();
          } catch {
            createdIso = new Date().toISOString();
          }
          const monthVal = m.declaration_month;
          const monthName =
            typeof monthVal === "number"
              ? monthNames[(monthVal - 1 + 12) % 12]
              : monthVal || "";
          const yearVal = m.declaration_year;
          return {
            ...m,
            CREATEDTIME: createdIso,
            declaration_month: monthName,
            declaration_year:
              typeof yearVal === "number" ? String(yearVal) : yearVal || "",
          };
        };
        try {
          const result = await baseQuery({
            url: "mvds",
            params: {
              pageSize,
              ...(nextToken ? { nextToken } : {}),
              page,
              ...(
                companyProfileId ? { company_profile_id: companyProfileId } : {}
              ),
              limit
            },
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          const list = Array.isArray(data.data) ? data.data.map(norm) : [];
          return { data: { ...data, data: list } };
        } catch (error) {
          console.error("Error fetching MVDs:", error);
          return { data: { data: [] } };
        }
      },
      providesTags: ["MVD"],
    }),
    // Get all MVDs
    getMVDCompliances: builder.query({
      async queryFn(
        { pageSize = 100, nextToken = undefined, page = 1, searchQuery = "", createdFrom = "", createdTo = "", limit = 100 } = {},
        queryApi,
        extraOptions,
        baseQuery
      ) {
        try {
          const result = await baseQuery({
            url: "mvds/compliance",
            params: { 
              page,
              limit,
              ...(searchQuery && { search: searchQuery }),
              ...(createdFrom && { createdFrom }),
              ...(createdTo && { createdTo }),
            },
          });

          // console.log("MVDs API Response:", result);
          return result;
        } catch (error) {
          console.error("Error fetching MVDs:", error);
          // Return empty data if the API fails
          return {
            data: {
              data: [],
            },
          };
        }
      },
      providesTags: ["MVD"],
    }),

    getAllMVDs: builder.query({
      async queryFn(arg, queryApi, extraOptions, baseQuery) {
        const monthNames = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const norm = (m) => {
          const created = m.createdAt || m.CREATEDTIME;
          let createdIso;
          try {
            createdIso = created
              ? new Date(created).toISOString()
              : new Date().toISOString();
          } catch {
            createdIso = new Date().toISOString();
          }
          const updated = m.updatedAt || m.MODIFIEDTIME;
          let updatedIso;
          try {
            updatedIso = updated ? new Date(updated).toISOString() : createdIso;
          } catch {
            updatedIso = createdIso;
          }
          const monthVal = m.declaration_month;
          const monthName =
            typeof monthVal === "number"
              ? monthNames[(monthVal - 1 + 12) % 12]
              : monthVal || "";
          const yearVal = m.declaration_year;
          const company = m.company_profile_id;
          const companyName =
            typeof company === "object" && company !== null
              ? company.company_name || "Unknown Company"
              : undefined;
          return {
            ...m,
            // company_profile_id: companyId || m.company_profile_id,
            companyName,
            CREATEDTIME: createdIso,
            MODIFIEDTIME: updatedIso,
            declaration_month: monthName,
            declaration_year:
              typeof yearVal === "number" ? String(yearVal) : yearVal || "",
          };
        };
        try {
          // Support both search query string and object with parameters
          const searchQuery = typeof arg === 'string' ? arg : arg?.searchQuery || '';
          const companyProfileId = typeof arg === 'object' ? arg?.companyProfileId : undefined;
          const createdFrom = typeof arg === "object" ? arg?.createdFrom : undefined;
          const createdTo = typeof arg === "object" ? arg?.createdTo : undefined;
          const page = typeof arg === 'object' ? arg?.page : undefined;
          const limit = typeof arg === 'object' ? arg?.limit : undefined;
          
          const result = await baseQuery({
            url: "mvds",
            params: { 
              ...(companyProfileId ? { company_profile_id: companyProfileId } : {}),
              ...(searchQuery ? { search: searchQuery } : {}),
              ...(createdFrom ? { createdFrom } : {}),
              ...(createdTo ? { createdTo } : {}),
              ...(page ? { page } : {}),
              ...(limit ? { limit } : {}),
            },
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          const list = Array.isArray(data.data) ? data.data.map(norm) : [];
          return { data: { ...data, data: list } };
        } catch (error) {
          console.error("Error fetching MVDs:", error);
          return { data: { data: [] } };
        }
      },
      providesTags: ["MVD"],
    }),
    // Get a single MVD by ID
    getMVDById: builder.query({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        const monthNames = [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ];
        const normOne = (m) => {
          if (!m) return m;
          const created = m.createdAt || m.CREATEDTIME;
          let createdIso;
          try {
            createdIso = created
              ? new Date(created).toISOString()
              : new Date().toISOString();
          } catch {
            createdIso = new Date().toISOString();
          }
          const monthVal = m.declaration_month;
          const monthName =
            typeof monthVal === "number"
              ? monthNames[(monthVal - 1 + 12) % 12]
              : monthVal || "";
          const yearVal = m.declaration_year;
          const statusName =
            (m?.status && (m.status.name || m.status.status_name)) ||
            m.status_name ||
            "";
          const statusId =
            typeof m?.status === "object" && m.status !== null
              ? m.status.id || m.status._id
              : m?.status;
          const eprCertificateFiles = Array.isArray(m?.epr_certficate_files)
            ? m.epr_certficate_files
            : Array.isArray(m?.epr_certificate_files)
              ? m.epr_certificate_files
              : [];
          const quarterlyCertificateFiles = Array.isArray(
            m?.quarterly_certificate_files,
          )
            ? m.quarterly_certificate_files
            : Array.isArray(m?.certificate_files)
              ? m.certificate_files
              : [];
          return {
            ...m,
            status_name: statusName,
            status_id: statusId,
            epr_certficate_files: eprCertificateFiles,
            epr_certificate_files: eprCertificateFiles,
            quarterly_certificate_files: quarterlyCertificateFiles,
            certificate_files: quarterlyCertificateFiles,
            CREATEDTIME: createdIso,
            declaration_month: monthName,
            declaration_year:
              typeof yearVal === "number" ? String(yearVal) : yearVal || "",
          };
        };
        try {
          const result = await baseQuery({ url: `mvds/${id}` });
          if (result.error) return { error: result.error };
          const payload = result.data || {};
          const data = payload.data ? normOne(payload.data) : null;
          return { data: { ...payload, data } };
        } catch (error) {
          console.error(`Error fetching MVD with ID ${id}:`, error);
          return { data: null };
        }
      },
      providesTags: (result, error, id) => [{ type: "MVD", id }],
    }),
    // Get a single MVD Complianceby ID
    getMVDComplianceById: builder.query({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `mvds/compliance/${id}`,
          });

          return result;
        } catch (error) {
          console.error(`Error fetching MVD with ID ${id}:`, error);
          return { data: null };
        }
      },
      providesTags: (result, error, id) => [{ type: "MVD", id }],
    }),

    // Get MVDs filtered by company profile ID
    getCompanyMVDComplianceList: builder.query({
      async queryFn(companyProfileId, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "mvds/compliance",
            params: { company_profile_id: companyProfileId },
          });

          if (result.error) return { error: result.error };

          // Filter MVDs that match the company profile ID
          const data = result.data || {};
          const allMVDs = Array.isArray(data.data) ? data.data : [];
          const filteredMVDs = allMVDs.filter((mvd) => {
            const mvdCompanyId =
              typeof mvd.company_profile_id === "object" &&
              mvd.company_profile_id !== null
                ? mvd.company_profile_id.id || mvd.company_profile_id._id
                : mvd.company_profile_id;
            return mvdCompanyId === companyProfileId;
          });

          return { data: { ...data, data: filteredMVDs } };
        } catch (error) {
          console.error(
            `Error fetching MVDs for company ${companyProfileId}:`,
            error
          );
          return { data: { data: [] } };
        }
      },
      providesTags: (result, error, id) => [{ type: "MVD", id }],
    }),

    // Get latest MVD
    getLatestMVD: builder.query({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        if (!id) return { data: null };

        try {
          const result = await baseQuery({
            url: `mvds/latest/${id}`,
          });

          return result;
        } catch (error) {
          console.error(`Error fetching latest MVD:`, error);
          return { data: null };
        }
      },
      providesTags: (result, error, id) => [{ type: "MVD", id }],
    }),

    // Create a new MVD
    createMVD: builder.mutation({
      async queryFn(mvd, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "mvds",
            method: "POST",
            body: mvd,
          });
          if (result.error) return { error: result.error };
          const data = result.data || {};
          // Normalize to include success flag for existing UI checks
          return { data: { ...data, success: true } };
        } catch (error) {
          console.error("Error creating MVD:", error);
          return { error: "Failed to create MVD" };
        }
      },
      invalidatesTags: ["MVD"],
    }),
    // Update an MVD
    updateMVD: builder.mutation({
      async queryFn({ id, formData }, queryApi, extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `mvds/${id}`,
          method: "PUT",
          body: formData,
        });
        if (result.error) return { error: result.error };
        const data = result.data || {};
        return { data: { ...data, success: true } };
      },
      invalidatesTags: (result, error, { id }) => [{ type: "MVD", id }, "MVD"],
    }),
    // Delete an MVD
    deleteMVD: builder.mutation({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `mvds/${id}`,
            method: "DELETE",
          });

          return result;
        } catch (error) {
          console.error(`Error deleting MVD with ID ${id}:`, error);
          return { error: "Failed to delete MVD" };
        }
      },
      invalidatesTags: ["MVD"],
    }),
    bulkDeleteMVD: builder.mutation({
      async queryFn(ids, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `mvds/bulk/delete`,
            method: "DELETE",
            body: { mvdIds: ids },
          });

          return result;
        } catch (error) {
          console.error(`Error bulk deleting MVDs:`, error);
          return { error: "Failed to bulk delete MVDs" };
        }
      },
      invalidatesTags: ["MVD"],
    }),
    // Delete Quarterly Compliance
    deleteQuarterlyCompliance: builder.mutation({
      async queryFn(id, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `mvds/compliance/${id}`,
            method: "DELETE",
          });

          return result;
        } catch (error) {
          console.error(`Error deleting MVD with ID ${id}:`, error);
          return { error: "Failed to delete MVD" };
        }
      },
      invalidatesTags: ["MVD"],
    }),

    // Get companies
    getCompanies: builder.query({
      async queryFn(arg, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "profiles",
          });

          return result;
        } catch (error) {
          console.error("Error fetching companies:", error);
          return { data: [] };
        }
      },
      providesTags: ["Companies"],
    }),

    // Trigger a status transition
    triggerStatusTransition: builder.mutation({
      async queryFn(
        { mvdId, transitionId, ...patch },
        queryApi,
        extraOptions,
        baseQuery
      ) {
        try {
          const result = await baseQuery({
            url: `mvds/${mvdId}/transition/${transitionId}`,
            method: "POST",
            body: patch,
          });

          return result;
        } catch (error) {
          console.error(`Error transitioning MVD:`, error);
          return { error: "Failed to transition MVD" };
        }
      },
      invalidatesTags: (result, error, { mvdId }) => [
        { type: "MVD", id: mvdId },
        "MVD",
      ],
    }),

    // Revert to previous status
    revertMVDStatus: builder.mutation({
      async queryFn({ mvdId }, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: `mvds/${mvdId}/revert`,
            method: "POST",
          });

          return result;
        } catch (error) {
          console.error(`Error reverting MVD:`, error);
          return { error: "Failed to revert MVD" };
        }
      },
      invalidatesTags: (result, error, { mvdId }) => [
        { type: "MVD", id: mvdId },
        "MVD",
      ],
    }),
    setManualMVDStatus: builder.mutation({
      async queryFn(
        { mvdId, targetStatusId, reason = "" },
        queryApi,
        extraOptions,
        baseQuery
      ) {
        try {
          const result = await baseQuery({
            url: `mvds/${mvdId}/manual-status`,
            method: "POST",
            body: { targetStatusId, reason },
          });
          return result;
        } catch (error) {
          console.error("Error manually updating MVD status:", error);
          return { error: "Failed to manually update MVD status" };
        }
      },
      invalidatesTags: (result, error, { mvdId }) => [
        { type: "MVD", id: mvdId },
        "MVD",
      ],
    }),

    // Update these endpoints to call the real API instead of returning mock data

    // Get all MVD statuses
    getMVDStatuses: builder.query({
      async queryFn(arg, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "mvd-statuses",
          });

          // console.log("MVD Statuses API Response:", result);

          return result;
        } catch (error) {
          console.error("Error fetching MVD statuses:", error);
          return { data: { data: [] } };
        }
      },
    }),

    // Get all MVD transitions
    getMVDTransitions: builder.query({
      async queryFn(arg, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "mvd-transitions",
          });

          // console.log("MVD Transitions API Response:", result);
          return result;
        } catch (error) {
          console.error("Error fetching MVD transitions:", error);
          return { data: { data: [] } };
        }
      },
    }),

    // Get all packaging subcategories
    getPackagingSubcategories: builder.query({
      async queryFn(arg, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "packaging-subcategories",
          });

          // console.log("Packaging Subcategories API Response:", result);
          return result;
        } catch (error) {
          console.error("Error fetching packaging subcategories:", error);
          return { data: { data: [] } };
        }
      },
    }),

    // Get all EPR subcategory fees
    getEPRSubcategoryFees: builder.query({
      async queryFn(arg, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "epr-subcategory-fees",
          });

          // console.log("EPR Subcategory Fees API Response:", result);
          return result;
        } catch (error) {
          console.error("Error fetching EPR subcategory fees:", error);
          return { data: { data: [] } };
        }
      },
    }),

    // Get all waste streams
    getWasteStreams: builder.query({
      async queryFn(arg, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "waste-streams",
          });

          // console.log("Waste Streams API Response:", result);
          return result;
        } catch (error) {
          console.error("Error fetching waste streams:", error);
          return { data: { data: [] } };
        }
      },
    }),

    // For backward compatibility - alias to getEPRSubcategoryFees
    getSubcategoryFees: builder.query({
      async queryFn(arg, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "epr-subcategory-fees",
          });

          // console.log("Subcategory Fees API Response:", result);
          return result;
        } catch (error) {
          console.error("Error fetching subcategory fees:", error);
          return { data: { data: [] } };
        }
      },
    }),

    // Get monthly report data
    getReportData: builder.query({
      async queryFn(arg, queryApi, extraOptions, baseQuery) {
        try {
          const result = await baseQuery({
            url: "reports/monthly",
          });

          // console.log("Monthly Report Data API Response:", result);
          return result;
        } catch (error) {
          console.error("Error fetching monthly report data:", error);
          return { data: { data: [] } };
        }
      },
    }),
  }),
});

// Export all the hooks
export const {
  useGetMVDsQuery,
  useGetAllMVDsQuery,
  useLazyGetAllMVDsQuery,
  useGetMVDByIdQuery,
  useGetMVDCompliancesQuery,
  useLazyGetMVDCompliancesQuery,
  useGetMVDComplianceByIdQuery,
  useGetCompanyMVDComplianceListQuery,
  useGetLatestMVDQuery,
  useCreateMVDMutation,
  useUpdateMVDMutation,
  useDeleteMVDMutation,
  useBulkDeleteMVDMutation,
  useDeleteQuarterlyComplianceMutation,
  useTriggerStatusTransitionMutation,
  useRevertMVDStatusMutation,
  useSetManualMVDStatusMutation,
  useGetCompaniesQuery,
  useGetMVDStatusesQuery,
  useGetMVDTransitionsQuery,
  useGetPackagingSubcategoriesQuery,
  useGetEPRSubcategoryFeesQuery,
  useGetSubcategoryFeesQuery,
  useGetWasteStreamsQuery,
  useGetReportDataQuery,
} = mvdApi;
